import { MemberSignInCard } from './connections/MemberSignInCard'
import { ActivityRetentionBanner } from './connections/ActivityRetentionBanner'
import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { ApiError, getJSON } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { displayDateTime } from '@/lib/dates'
import { HealthBadge } from './connections/ConnectionCells'
import { connectionTypeName } from './connections/types'
import { words, type ConnectionDetail } from './connections/detailTypes'
import {
  useConnectionDetail,
  useDetailAction,
  reconnectConnection,
} from './connections/useConnectionDetail'
import {
  ConnectionDetailHeader,
  headerShowsFilledButton,
} from './connections/ConnectionDetailHeader'
import { ConnectionFacts } from './connections/ConnectionFacts'
import { ConnectionRuns } from './connections/ConnectionRuns'
import { ConnectionErrors, ConnectionActivity } from './connections/ConnectionHistory'
import { ConnectionEditDialog } from './connections/ConnectionEditDialog'
import { ConnectionSignInLog } from './connections/ConnectionSignInLog'
import type { MemberSignInResponse } from './connections/memberSignInTypes'
import { NoAccessPage } from './NoAccessPage'

export function ConnectionDetailPage({ connectionId }: { connectionId: number }) {
  const { connection, types, runs } = useConnectionDetail(connectionId)
  const action = useDetailAction(connectionId)
  const reconnect = useMutation({ mutationFn: () => reconnectConnection(connectionId) })
  const [dialog, setDialog] = useState<string | null>(null)
  const [tab, setTab] = useConnectionTab()
  const viewOnly = useViewOnly()
  if (connection.isPending)
    return (
      <DetailPage title="Connection">
        <p>Loading connection…</p>
      </DetailPage>
    )
  if (connection.isError) return <ConnectionReadError error={connection.error} />
  const row = connection.data.connection
  const type = types.data?.connection_types.find((item) => item.type === row.connection_type)
  const onAction = (name: string) => {
    if (viewOnly) return
    if (name === 'reconnect') {
      if (type?.credential_method === 'oauth') reconnect.mutate()
      else setDialog('rotate')
    } else if (['rename', 'remove', 'import', 'retention'].includes(name)) setDialog(name)
    else action.mutate({ action: name })
  }
  const pending = action.isPending || reconnect.isPending || runs.isPending
  const header = (
    <ConnectionDetailHeader
      row={row}
      type={type}
      runs={runs.data?.sync_runs ?? []}
      pending={pending}
      onAction={onAction}
    />
  )
  return (
    <DetailPage
      title={row.display_name}
      subtitle={<ConnectionSubtitle row={row} />}
      badge={
        <>
          <HealthBadge row={row} />
          {row.status === 'active' && <Badge variant="neutral">Active</Badge>}
        </>
      }
      backHref="/settings/connections"
      backLabel="Connections"
      readOnlyActions={header}
      actions={header}
    >
      <div className="space-y-4">
        <ActionErrors errors={[action.error, reconnect.error]} typesFailed={types.isError} />
        <DetailAttention
          row={row}
          disabled={viewOnly || pending || !type}
          onReconnect={() => onAction('reconnect')}
        />
        <ActivityRetentionBanner
          row={row}
          onChoose={viewOnly ? undefined : () => onAction('retention')}
        />
        <ConnectionFacts
          row={row}
          onEdit={() => onAction('import')}
          onRetention={() => onAction('retention')}
          onErrors={() => setTab('errors')}
        />
        <MemberSignInCard
          connectionId={connectionId}
          filledAllowed={!headerShowsFilledButton(row)}
        />
        <HistoryTabs
          connectionId={connectionId}
          errorCount={row.error_count}
          runs={runs}
          tab={tab}
          setTab={setTab}
        />
      </div>
      {dialog && (
        <ConnectionEditDialog
          key={`${row.id}-${dialog}`}
          row={row}
          action={dialog}
          type={type}
          onClose={() => setDialog(null)}
        />
      )}
    </DetailPage>
  )
}
function useConnectionTab() {
  const [tab, setTab] = useState(() =>
    ['#errors', '#sign-in-log'].includes(window.location.hash)
      ? window.location.hash.slice(1)
      : 'runs',
  )
  function selectTab(next: string) {
    setTab(next)
    const url = new URL(window.location.href)
    if (next === 'sign-in-log') url.hash = 'sign-in-log'
    else if (url.hash === '#sign-in-log') url.hash = ''
    window.history.replaceState({}, '', url)
  }
  return [tab, selectTab] as const
}
function ConnectionReadError({ error }: { error: Error }) {
  if (error instanceof ApiError && error.status === 403) return <NoAccessPage />
  return (
    <DetailPage title="Connection">
      <Alert>Could not load connection</Alert>
    </DetailPage>
  )
}
function ActionErrors({ errors, typesFailed }: { errors: (Error | null)[]; typesFailed: boolean }) {
  return (
    <>
      {errors.map((error, index) => error && <Alert key={index}>{error.message}</Alert>)}
      {typesFailed && <Alert>Could not load credential options</Alert>}
    </>
  )
}
function HistoryTabs({
  connectionId,
  errorCount,
  runs,
  tab,
  setTab,
}: {
  connectionId: number
  errorCount: number
  runs: ReturnType<typeof useConnectionDetail>['runs']
  tab: string
  setTab: (tab: string) => void
}) {
  const signIn = useQuery({
    queryKey: ['connection-member-sign-in-tab', connectionId],
    queryFn: () =>
      getJSON<MemberSignInResponse>(`/api/v1/connections/${connectionId}/member_sign_in`),
    retry: false,
  })
  const supportsSignIn = signIn.data?.member_sign_in?.supported === true
  return (
    <Tabs
      value={tab === 'sign-in-log' && !supportsSignIn ? 'runs' : tab}
      onValueChange={setTab}
      className="grid min-w-0 grid-cols-1"
    >
      <TabsList>
        <TabsTrigger value="runs">Runs</TabsTrigger>
        <TabsTrigger value="errors">Errors{errorCount > 0 ? ` · ${errorCount}` : ''}</TabsTrigger>
        <TabsTrigger value="activity">Activity</TabsTrigger>
        {supportsSignIn && <TabsTrigger value="sign-in-log">Sign-in log</TabsTrigger>}
      </TabsList>
      <TabsContent value="runs">
        {runs.isPending ? (
          <p>Loading runs…</p>
        ) : runs.isError ? (
          <Alert>Could not load runs</Alert>
        ) : (
          <ConnectionRuns runs={runs.data.sync_runs} />
        )}
      </TabsContent>
      <TabsContent value="errors">
        <ConnectionErrors id={connectionId} />
      </TabsContent>
      <TabsContent value="activity">
        <ConnectionActivity id={connectionId} />
      </TabsContent>
      {supportsSignIn && (
        <TabsContent value="sign-in-log">
          <ConnectionSignInLog connectionId={connectionId} />
        </TabsContent>
      )}
    </Tabs>
  )
}
function ConnectionSubtitle({ row }: { row: ConnectionDetail }) {
  const vendor = connectionTypeName(row.connection_type)
  return (
    <>
      {vendor}
      {row.external_url && (
        <>
          <br />
          <a
            href={row.external_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Open in {vendor}
            <ExternalLink className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
          </a>
        </>
      )}
    </>
  )
}
function DetailAttention({
  row,
  disabled,
  onReconnect,
}: {
  row: ConnectionDetail
  disabled: boolean
  onReconnect: () => void
}) {
  if (row.health !== 'needs_attention' || row.status !== 'active') return null
  const reasons: Record<string, string> = {
    credentials_rejected: 'Credentials rejected',
    record_errors: 'More than 10% of records errored',
    consecutive_failures: 'Three consecutive failed runs',
  }
  return (
    <Alert className="flex items-center justify-between gap-3">
      <span>
        <span className="font-medium">{row.display_name} needs attention</span> ·{' '}
        {reasons[row.needs_attention_reason ?? ''] ??
          words(row.needs_attention_reason ?? 'Needs attention')}
        {row.needs_attention_since && <> · since {displayDateTime(row.needs_attention_since)}</>}
      </span>
      <Button variant="outline" disabled={disabled} onClick={onReconnect}>
        Reconnect
      </Button>
    </Alert>
  )
}
